/**
 * Validadores de payloads para APIs e Corel
 * 
 * Origem: linhas 672-701, 760-809 do useSaveController.ts
 */

/**
 * Resultado de validação
 */
export interface ValidationResult {
  /**
   * Se a validação passou
   */
  ok: boolean;
  
  /**
   * Campos ausentes ou inválidos
   */
  missing: string[];
  
  /**
   * Mensagem de erro (opcional)
   */
  error?: string;
}

/**
 * Valida payload da API antes do envio
 * 
 * @param payload - Payload a validar
 * @returns Resultado da validação
 */
export function validateApiPayload(payload: any): ValidationResult {
  const missing: string[] = [];
  const isValidString = (v: any) => typeof v === 'string' && v.trim().length > 0;
  const isValidNumber = (v: any) => typeof v === 'number' && !Number.isNaN(v);
  const isValidBoolean = (v: any) => typeof v === 'boolean';

  // Campos obrigatórios
  if (!isValidNumber(payload?.orc_id)) missing.push('orc_id');
  if (!isValidNumber(payload?.oit_id)) missing.push('oit_id');
  if (!isValidNumber(payload?.pro_id) || payload?.pro_id === 0) missing.push('pro_id');
  if (!isValidNumber(payload?.prod_type)) missing.push('prod_type');
  if (!isValidString(payload?.nome_arquivo)) missing.push('nome_arquivo');
  if (!isValidString(payload?.caminho_arquivo)) missing.push('caminho_arquivo');
  if (!isValidBoolean(payload?.sys_save)) missing.push('sys_save');
  if (!isValidBoolean(payload?.sys_export)) missing.push('sys_export');
  if (!isValidString(payload?.sys_update)) missing.push('sys_update');
  
  // Validar objetos
  if (!Array.isArray(payload?.objetos) || payload.objetos.length === 0) {
    missing.push('objetos[]');
  } else {
    payload.objetos.forEach((o: any, idx: number) => {
      if (!isValidString(o?.objeto_id)) missing.push(`objetos[${idx}].objeto_id`);
      if (!isValidString(o?.nome)) missing.push(`objetos[${idx}].nome`);
      if (!isValidNumber(o?.quantidade)) missing.push(`objetos[${idx}].quantidade`);
      // guid, largura, altura, contracao, horario_entrega são opcionais
    });
  }

  return {
    ok: missing.length === 0,
    missing,
    error: missing.length > 0 ? `Campos obrigatórios ausentes: ${missing.join(', ')}` : undefined,
  };
}

/**
 * Valida dados antes de enviar para o Corel
 * 
 * @param workingData - Dados do salvamento
 * @returns Resultado da validação com erros detalhados
 */
export function validateCorelPayload(workingData: any): ValidationResult {
  const errors: string[] = [];
  
  // Validações críticas
  if (!workingData.caminho_arquivo) {
    errors.push('caminho_arquivo é obrigatório');
  }
  
  if (!workingData.pedido?.orc_id) {
    errors.push('orc_id é obrigatório');
  }
  
  if (!workingData.pedido?.oit_id) {
    errors.push('oit_id é obrigatório');
  }
  
  if (!workingData.usuario) {
    errors.push('Usuário não identificado (obrigatório para auditoria)');
  }
  
  if (!workingData.objetos || workingData.objetos.length === 0) {
    errors.push('Nenhum objeto para salvar');
  }
  
  // Validar cada objeto
  if (workingData.objetos && workingData.objetos.length > 0) {
    for (let idx = 0; idx < workingData.objetos.length; idx++) {
      const obj = workingData.objetos[idx];
      
      if (!obj.guid) {
        errors.push(`Objeto ${idx}: guid ausente (executar organizar-camada primeiro)`);
      }
      
      if (!obj.id && !obj.staticId) {
        errors.push(`Objeto ${idx}: staticId ausente`);
      }
      
      if (!obj.nome) {
        errors.push(`Objeto ${idx}: nome ausente`);
      }
    }
  }

  return {
    ok: errors.length === 0,
    missing: errors,
    error: errors.length > 0 ? `SAVE_ERROR:VALIDAÇÃO: ${errors.join('; ')}` : undefined,
  };
}

/**
 * Valida formato do caminho de arquivo retornado pelo Corel
 * 
 * @param caminhoSalvo - Caminho retornado pelo Corel
 * @returns Resultado da validação
 */
export function validateFilePath(caminhoSalvo: string): ValidationResult {
  const errors: string[] = [];
  
  if (!caminhoSalvo) {
    errors.push('Caminho está vazio ou undefined');
  } else {
    if (!caminhoSalvo.includes('\\')) {
      errors.push('Caminho não contém separador de diretório (\\)');
    }
    
    if (!caminhoSalvo.toLowerCase().endsWith('.cdr')) {
      errors.push('Caminho não termina com .cdr');
    }
  }

  return {
    ok: errors.length === 0,
    missing: errors,
    error: errors.length > 0 ? `Caminho de arquivo inválido: ${errors.join('; ')}` : undefined,
  };
}

/**
 * Valida se metadados coletados são válidos
 * 
 * @param metadata - Metadados retornados pelo Corel
 * @returns Resultado da validação
 */
export function validateMetadata(metadata: any): ValidationResult {
  const errors: string[] = [];
  
  if (!metadata) {
    errors.push('Metadados ausentes');
    return { ok: false, missing: errors, error: 'Metadados não foram coletados' };
  }
  
  const result = metadata.result || metadata.data;
  if (!result) {
    errors.push('Metadados não contêm "result" ou "data"');
  }
  
  const objetos = result?.objetos || [];
  if (!Array.isArray(objetos) || objetos.length === 0) {
    errors.push('Nenhum objeto encontrado nos metadados');
  }

  return {
    ok: errors.length === 0,
    missing: errors,
    error: errors.length > 0 ? errors.join('; ') : undefined,
  };
}

/**
 * Valida credenciais SMB
 * 
 * @param credentials - Credenciais SMB
 * @returns Resultado da validação
 */
export function validateSmbCredentials(credentials: any): ValidationResult {
  const errors: string[] = [];
  
  if (!credentials) {
    errors.push('Credenciais ausentes');
    return { ok: false, missing: errors, error: 'Credenciais SMB não fornecidas' };
  }
  
  if (!credentials.username || typeof credentials.username !== 'string') {
    errors.push('username inválido ou ausente');
  }
  
  if (!credentials.password || typeof credentials.password !== 'string') {
    errors.push('password inválido ou ausente');
  }
  
  // domain é opcional, mas se fornecido deve ser string
  if (credentials.domain !== undefined && typeof credentials.domain !== 'string') {
    errors.push('domain deve ser string');
  }

  return {
    ok: errors.length === 0,
    missing: errors,
    error: errors.length > 0 ? `Credenciais SMB inválidas: ${errors.join('; ')}` : undefined,
  };
}

/**
 * Valida IDs de pedido/item
 * 
 * @param orcId - ID do orçamento
 * @param oitId - ID do item
 * @returns Resultado da validação
 */
export function validatePedidoIds(orcId: any, oitId: any): ValidationResult {
  const errors: string[] = [];
  
  const isValidOrcId = typeof orcId === 'number' && !isNaN(orcId) && orcId > 0;
  const isValidOitId = typeof oitId === 'number' && !isNaN(oitId) && oitId > 0;
  
  if (!isValidOrcId) {
    errors.push(`orc_id inválido: ${orcId} (esperado: número > 0)`);
  }
  
  if (!isValidOitId) {
    errors.push(`oit_id inválido: ${oitId} (esperado: número > 0)`);
  }

  return {
    ok: errors.length === 0,
    missing: errors,
    error: errors.length > 0 ? errors.join('; ') : undefined,
  };
}

